import type { Activity, LeaderboardEntry, Participant, PlayerScore, WordPlacement } from "./types";

export const ICONS = ["🐼", "🦊", "🐯", "🐸", "🐧", "🐨", "🦁", "🐙", "🦄", "🐝", "🦋", "🐳"];
export const JOIN_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export function normalizeAnswer(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("en");
}

export function shuffleText(value: string, random: () => number = Math.random): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized.split(" ").map((word) => {
    const letters = [...word];
    if (letters.length < 2) return word;
    for (let index = letters.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [letters[index], letters[swapIndex]] = [letters[swapIndex], letters[index]];
    }
    if (letters.join("").toLocaleLowerCase("en") === word.toLocaleLowerCase("en")) {
      [letters[0], letters[1]] = [letters[1], letters[0]];
    }
    return letters.join("");
  }).join(" ");
}

function randomIndex(max: number): number {
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const values = new Uint32Array(1);
  do crypto.getRandomValues(values); while (values[0] >= limit);
  return values[0] % max;
}

export function randomCode(length: number): string {
  return Array.from({ length }, () => JOIN_ALPHABET[randomIndex(JOIN_ALPHABET.length)]).join("");
}

export function shuffledNumbers(count: number): number[] {
  const numbers = Array.from({ length: 100 }, (_, index) => index + 1);
  for (let index = numbers.length - 1; index > 0; index -= 1) {
    const swapIndex = randomIndex(index + 1);
    [numbers[index], numbers[swapIndex]] = [numbers[swapIndex], numbers[index]];
  }
  return numbers.slice(0, count);
}

function seededFromText(seed: string): () => number {
  let state = 2166136261;
  for (const char of seed) {
    state ^= char.charCodeAt(0);
    state = Math.imul(state, 16777619);
  }
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 0x1_0000_0000;
  };
}

export function createPuzzlePieces(seed: string, count = 9): number[] {
  const pieces = Array.from({ length: count }, (_, index) => index);
  const random = seededFromText(seed);
  // Sattolo's algorithm produces one cycle, so no piece can start in its
  // correct position. A participant-specific seed also survives refreshes.
  for (let index = pieces.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * index);
    [pieces[index], pieces[swapIndex]] = [pieces[swapIndex], pieces[index]];
  }
  return pieces;
}

export function rankScores(
  activity: Activity,
  participants: Record<string, Participant>,
  eligibleIds: string[],
  scores: Record<string, PlayerScore>,
  startedAt: number,
): LeaderboardEntry[] {
  const rows = eligibleIds.map((participantId) => {
    const participant = participants[participantId];
    const score = scores[participantId] ?? { participantId, completed: false, progress: 0, attempts: 0 };
    return { participant, score };
  });

  rows.sort((a, b) => {
    if (a.score.completed !== b.score.completed) return a.score.completed ? -1 : 1;
    if (activity.type === "guess-number" && a.score.completed && b.score.completed) {
      if (a.score.attempts !== b.score.attempts) return a.score.attempts - b.score.attempts;
    } else if (a.score.progress !== b.score.progress) {
      return b.score.progress - a.score.progress;
    }
    const aTime = a.score.completedAt ?? Number.MAX_SAFE_INTEGER;
    const bTime = b.score.completedAt ?? Number.MAX_SAFE_INTEGER;
    if (aTime !== bTime) return aTime - bTime;
    return a.participant.joinedAt - b.participant.joinedAt;
  });

  return rows.map(({ participant, score }, index) => ({
    rank: index + 1,
    participantId: participant.id,
    nickname: participant.nickname,
    icon: participant.icon,
    completed: score.completed,
    progress: score.progress,
    attempts: score.attempts,
    elapsedMs: score.completedAt ? Math.max(0, score.completedAt - startedAt) : undefined,
  }));
}

const DIRECTIONS = [
  [0, 1],
  [1, 0],
  [1, 1],
  [1, -1],
] as const;

function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x1_0000_0000;
  };
}

export function generateWordSearch(wordsInput: string[], shuffleVersion = 0): { grid: string[]; placements: WordPlacement[] } {
  const words = wordsInput.map((word) => word.toUpperCase().replace(/[^A-Z]/g, "")).filter(Boolean);
  const longest = Math.max(...words.map((word) => word.length), 8);
  const size = Math.min(14, Math.max(10, longest + 2));
  for (let attempt = 1; attempt <= 80; attempt += 1) {
    const random = seeded(words.join("").split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) + attempt + shuffleVersion * 1009);
    const grid = Array.from({ length: size }, () => Array<string>(size).fill(""));
    const placements: WordPlacement[] = [];
    let success = true;
    for (const word of [...words].sort((a, b) => b.length - a.length)) {
      let placed = false;
      for (let tries = 0; tries < 250 && !placed; tries += 1) {
        const [dr, dc] = DIRECTIONS[Math.floor(random() * DIRECTIONS.length)];
        const row = Math.floor(random() * size);
        const col = Math.floor(random() * size);
        const endRow = row + dr * (word.length - 1);
        const endCol = col + dc * (word.length - 1);
        if (endRow < 0 || endRow >= size || endCol < 0 || endCol >= size) continue;
        if ([...word].some((char, index) => grid[row + dr * index][col + dc * index] && grid[row + dr * index][col + dc * index] !== char)) continue;
        [...word].forEach((char, index) => { grid[row + dr * index][col + dc * index] = char; });
        placements.push({ word, startRow: row, startCol: col, endRow, endCol });
        placed = true;
      }
      if (!placed) { success = false; break; }
    }
    if (success) {
      const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      return {
        grid: grid.map((row) => row.map((char) => char || alphabet[Math.floor(random() * alphabet.length)]).join("")),
        placements,
      };
    }
  }
  throw new Error("Could not generate a word-search grid. Try shorter words.");
}
